import { useQuery } from 'react-query'
import { request } from 'Features/auth/axios-client'
import { WaterfallRow } from 'Common/Charts/StockWaterfall/stockWaterfall.walk'
import * as Types from './StockProjectionException.types'

const fetchStockProjectExceptionsData = async ({
  planId,
  sourcesiteId,
  weekRangeConfig,
  workcentresList,
}: Types.StockProjectionTableParams) => {
  const query = workcentresList?.join(',')
  const { data } = await request({
    method: 'post',
    url: `plan/${planId}/above-wc-stock-projections-exceptions-table?workcentres=${query}`,
    data: weekRangeConfig,
  })
  return data
}
export const useStockProjectExceptionsData = (
  queryParams: Types.StockProjectionTableParams
) => {
  return useQuery(
    [
      'stock-projection-exception-table',
      queryParams.planId,
      queryParams.sourcesiteId,
      queryParams.weekRangeConfig,
      queryParams.workcentresList?.join(','),
    ],
    () => fetchStockProjectExceptionsData(queryParams),
    {
      select: (data) => data?.data,
      refetchOnWindowFocus: false,
      enabled: queryParams.workcentresList?.length > 0,
      keepPreviousData: true,
    }
  )
}

const fetchStockProjectExceptionsGraphData = async ({
  planId,
  sourcesiteId,
  skuCodes,
  workcentresList,
}: Types.StockProjectionExceptionGraphURLParams) => {
  const query = workcentresList?.join(',')
  const { data } = await request({
    method: 'post',
    url: `plan/${planId}/above-wc-stock-projections-exceptions-graph?workcentres=${query}`,
    data: {
      sku_codes: skuCodes,
    },
  })
  return data
}
export const useStockProjectExceptionsGraphData = (
  queryParams: Types.StockProjectionExceptionGraphURLParams
) => {
  return useQuery(
    [
      'stock-projection-exception-graph',
      queryParams.planId,
      queryParams.sourcesiteId,
      queryParams.skuCodes,
      queryParams.workcentresList?.join(','),
    ],
    () => fetchStockProjectExceptionsGraphData(queryParams),
    {
      select: (data) => data?.data,
      enabled:
        queryParams.skuCodes?.length >= 0 &&
        queryParams.workcentresList?.length > 0,
      refetchOnWindowFocus: false,
      cacheTime: 0,
    }
  )
}

// Stock waterfall v2 (R2), every selected work centre at once. Constrained
// always: the arm Classic shows.
const fetchStockProjectionWaterfall = async ({
  planId,
  skuCodes,
  workcentresList,
}: Types.StockProjectionWaterfallParams) => {
  const query = workcentresList?.join(',')
  const { data } = await request({
    method: 'post',
    url: `plan/${planId}/above-wc-stock-projections-exceptions-waterfall?workcentres=${query}`,
    data: {
      sku_codes: skuCodes,
      planned_version: 'Constrained',
    },
  })
  return data
}

// Succeeds when at least one selected work centre has a ledger partition (the
// rows then come from those); fails with LEDGER_NOT_AVAILABLE when none has.
// An empty selection reads no ledger rows.
export const useStockProjectionWaterfallAvailability = (
  queryParams: Types.StockProjectionWaterfallParams
) => {
  return useQuery(
    [
      'stock-projection-waterfall-availability',
      queryParams.planId,
      queryParams.workcentresList?.join(','),
    ],
    () => fetchStockProjectionWaterfall({ ...queryParams, skuCodes: [] }),
    {
      enabled: queryParams.enabled && queryParams.workcentresList?.length > 0,
      refetchOnWindowFocus: false,
    }
  )
}

export const useStockProjectionWaterfallData = (
  queryParams: Types.StockProjectionWaterfallParams
) => {
  return useQuery(
    [
      'stock-projection-waterfall',
      queryParams.planId,
      queryParams.skuCodes,
      queryParams.workcentresList?.join(','),
    ],
    () => fetchStockProjectionWaterfall(queryParams),
    {
      select: (data): WaterfallRow[] => data?.data ?? [],
      enabled: queryParams.enabled && queryParams.workcentresList?.length > 0,
      refetchOnWindowFocus: false,
    }
  )
}
